/**
 * DriverFormWizard.jsx
 * Formulario de Motorista em Etapas Premium
 *
 * 7 Passos:
 * 1. Basico - Nome, Identificador (obrigatorio)
 * 2. Contato - CPF, Telefone, Email, Contato (CPF e phone em colunas dedicadas)
 * 3. Endereco - Endereco, Bairro, Cidade, Estado, CEP (em attributes.form)
 * 4. CNH - Numero CNH, Categoria, Validade, Tipo Sanguineo (licenseNumber em coluna dedicada)
 * 5. Acesso App - Senha para login no Driver App (opcional)
 * 6. Atributos - Atributos personalizados (opcional)
 * 7. Revisao - Confirmar dados
 *
 * Campos em colunas dedicadas: cpf, phone, licenseNumber, licenseExpiry, status
 * Campos em attributes.form: email, contact, address, neighborhood, city, state, postCode, documentCategory, bloodType
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box, Grid, Typography, Alert, Switch, FormControlLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
  Lock as LockIcon,
  Tune as TuneIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { driversActions } from '../../store';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import PremiumPhoneInput from '../../common/components/premium/PremiumPhoneInput';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';
import EditAttributesAccordion from './EditAttributesAccordion';
import DriverQrCodeDialog from './DriverQrCodeDialog';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const handleFormField = (data, updateData, key, value) => {
  updateData({
    attributes: {
      ...data.attributes,
      form: {
        ...data.attributes?.form,
        [key]: value,
      },
    },
  });
};

const getFormValue = (data, key) => data?.attributes?.form?.[key] || '';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('deviceIdentifier')} *`}
            value={data.uniqueId || ''}
            onChange={(e) => updateData({ uniqueId: e.target.value })}
            error={!!errors?.uniqueId}
            helperText={errors?.uniqueId}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: CONTATO (cpf e phone em colunas dedicadas)
// ============================================================================

const ContactStep = ({ data, updateData }) => {
  const t = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <PremiumInput
            label={t('attributeItin')}
            value={data.cpf || ''}
            onChange={(e) => updateData({ cpf: e.target.value.replace(/[^0-9.-]/g, '') })}
            helperText={t('driverAccountCpfHelp')}
            inputProps={{ maxLength: 14 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <PremiumPhoneInput
            label={t('sharedPhone')}
            value={data.phone || ''}
            onChange={(e) => updateData({ phone: e.target.value })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <PremiumInput
            label={t('userEmail')}
            value={getFormValue(data, 'email')}
            onChange={(e) => handleFormField(data, updateData, 'email', e.target.value)}
            type="email"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={12}>
          <PremiumInput
            label={t('deviceContact')}
            value={getFormValue(data, 'contact')}
            onChange={(e) => handleFormField(data, updateData, 'contact', e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 3: ENDERECO (tudo em attributes.form)
// ============================================================================

const AddressStep = ({ data, updateData }) => {
  const t = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 8 }}>
          <PremiumInput
            label={t('positionAddress')}
            value={getFormValue(data, 'address')}
            onChange={(e) => handleFormField(data, updateData, 'address', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <PremiumInput
            label={t('sharedNeighborhood')}
            value={getFormValue(data, 'neighborhood')}
            onChange={(e) => handleFormField(data, updateData, 'neighborhood', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 5 }}>
          <PremiumInput
            label={t('sharedCity')}
            value={getFormValue(data, 'city')}
            onChange={(e) => handleFormField(data, updateData, 'city', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <PremiumInput
            label={t('sharedState')}
            value={getFormValue(data, 'state')}
            onChange={(e) => handleFormField(data, updateData, 'state', e.target.value)}
            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <PremiumInput
            label={t('sharedPostalCode')}
            value={getFormValue(data, 'postCode')}
            onChange={(e) => handleFormField(data, updateData, 'postCode', e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 4: CNH (licenseNumber e licenseExpiry em colunas dedicadas)
// ============================================================================

const LicenseStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography
        variant="overline"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('sharedDriverLicence')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={t('sharedDocumentCNH')}
            value={data.licenseNumber || ''}
            onChange={(e) => updateData({ licenseNumber: e.target.value })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={t('deviceCategory')}
            value={getFormValue(data, 'documentCategory')}
            onChange={(e) => handleFormField(data, updateData, 'documentCategory', e.target.value.toUpperCase())}
            inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={t('sharedDocumentValidDate')}
            value={data.licenseExpiry ? new Date(data.licenseExpiry).toISOString().split('T')[0] : ''}
            onChange={(e) => updateData({ licenseExpiry: e.target.value ? new Date(e.target.value).toISOString() : null })}
            type="date"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={t('sharedBloodType')}
            value={getFormValue(data, 'bloodType')}
            onChange={(e) => handleFormField(data, updateData, 'bloodType', e.target.value.toUpperCase())}
            inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 5: ACESSO APP (opcional - cria conta de usuario para login)
// ============================================================================

const AppAccessStep = ({ data, updateData, errors }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const enableAppAccess = data._enableAppAccess || false;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert severity="info">
        {t('driverAccountPasswordInfo')}
      </Alert>

      <FormControlLabel
        control={(
          <Switch
            checked={enableAppAccess}
            onChange={(e) => updateData({ _enableAppAccess: e.target.checked })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: colors.accent },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent },
            }}
          />
        )}
        label={(
          <Typography sx={{ color: colors.textPrimary, fontWeight: 500 }}>
            {t('driverAccountEnableAccess')}
          </Typography>
        )}
      />

      {enableAppAccess && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PremiumInput
              label={`${t('userPassword')} *`}
              value={data._password || ''}
              onChange={(e) => updateData({ _password: e.target.value })}
              type="password"
              error={!!errors?._password}
              helperText={errors?._password}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PremiumInput
              label={`${t('driverAccountConfirmPassword')} *`}
              value={data._confirmPassword || ''}
              onChange={(e) => updateData({ _confirmPassword: e.target.value })}
              type="password"
              error={!!errors?._confirmPassword}
              helperText={errors?._confirmPassword}
            />
          </Grid>
        </Grid>
      )}

      {enableAppAccess && (
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          {t('driverAccountPasswordRequirements')}
        </Typography>
      )}
    </Box>
  );
};

// ============================================================================
// STEP 6: ATRIBUTOS
// ============================================================================

const AttributesStep = ({ data, updateData }) => {
  const t = useTranslation();

  // Driver-specific attribute definitions
  const driverAttributeDefinitions = {
    vehicleTypes: {
      name: t('sharedVehicleTypes') || 'Tipos de Veiculo',
      type: 'string',
    },
    notes: {
      name: t('sharedNotes') || 'Observacoes',
      type: 'string',
    },
    emergencyContact: {
      name: t('sharedEmergencyContact') || 'Contato de Emergencia',
      type: 'string',
    },
    emergencyPhone: {
      name: t('sharedEmergencyPhone') || 'Telefone de Emergencia',
      type: 'string',
    },
  };

  const handleSetAttributes = (newAttributes) => {
    updateData({ attributes: newAttributes });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <EditAttributesAccordion
        attributes={data.attributes || {}}
        setAttributes={handleSetAttributes}
        definitions={driverAttributeDefinitions}
      />
    </Box>
  );
};

// ============================================================================
// STEP 7: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const Section = ({ title, icon, children }) => (
    <Box
      sx={{
        backgroundColor: colors.surfaceElevated,
        borderRadius: tokens?.radius?.lg || '12px',
        border: `1px solid ${colors.borderSubtle}`,
        p: 2.5,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 18, color: colors.accent } })}
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
          }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );

  const InfoRow = ({ label, value, highlight }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: highlight ? colors.accent : colors.textPrimary,
            fontWeight: highlight ? 600 : 400,
            textAlign: 'right',
            maxWidth: '60%',
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const form = data?.attributes?.form || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Confirmacao */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      {/* Secoes */}
      <Grid container spacing={2}>
        {/* Identificacao */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('sharedRequired')} icon={<PersonIcon />}>
            <InfoRow label={t('sharedName')} value={data.name} highlight />
            <InfoRow label={t('deviceIdentifier')} value={data.uniqueId} />
          </Section>
        </Grid>

        {/* Contato */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('sharedContact')} icon={<PhoneIcon />}>
            <InfoRow label={t('attributeItin')} value={data.cpf} />
            <InfoRow label={t('sharedPhone')} value={data.phone} />
            <InfoRow label={t('userEmail')} value={form.email} />
            <InfoRow label={t('deviceContact')} value={form.contact} />
          </Section>
        </Grid>

        {/* Endereco */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('positionAddress')} icon={<HomeIcon />}>
            <InfoRow label={t('positionAddress')} value={form.address} />
            <InfoRow label={t('sharedNeighborhood')} value={form.neighborhood} />
            <InfoRow
              label={t('sharedCity')}
              value={[form.city, form.state].filter(Boolean).join(' - ')}
            />
            <InfoRow label={t('sharedPostalCode')} value={form.postCode} />
          </Section>
        </Grid>

        {/* CNH */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('sharedDriverLicence')} icon={<BadgeIcon />}>
            <InfoRow label={t('sharedDocumentCNH')} value={data.licenseNumber} />
            <InfoRow label={t('deviceCategory')} value={form.documentCategory} />
            <InfoRow
              label={t('sharedDocumentValidDate')}
              value={data.licenseExpiry ? new Date(data.licenseExpiry).toLocaleDateString() : ''}
            />
            <InfoRow label={t('sharedBloodType')} value={form.bloodType} />
          </Section>
        </Grid>

        {/* Acesso App */}
        {data._enableAppAccess && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Section title={t('driverAccountAccess')} icon={<LockIcon />}>
              <InfoRow label={t('driverAccountLoginEmail')} value={data.cpf?.replace(/[^0-9]/g, '')} />
              <InfoRow label={t('userPassword')} value="********" />
            </Section>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DriverFormWizard = ({ driver, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [submitError, setSubmitError] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [savedDriver, setSavedDriver] = useState(null);

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardBasicDriverDescription'),
      icon: <PersonIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        if (!data.uniqueId?.trim()) errors.uniqueId = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
        { label: t('deviceIdentifier'), value: data.uniqueId },
      ],
    },
    {
      label: t('sharedContact'),
      title: t('sharedContact'),
      description: t('wizardContactDescription'),
      icon: <PhoneIcon sx={{ fontSize: 18 }} />,
      component: ContactStep,
      optional: true,
      getSummary: (data) => [
        { label: t('attributeItin'), value: data.cpf },
        { label: t('sharedPhone'), value: data.phone },
        { label: t('userEmail'), value: data.attributes?.form?.email },
      ],
    },
    {
      label: t('positionAddress'),
      title: t('positionAddress'),
      description: t('wizardAddressDescription'),
      icon: <HomeIcon sx={{ fontSize: 18 }} />,
      component: AddressStep,
      optional: true,
      getSummary: (data) => [
        { label: t('sharedCity'), value: data.attributes?.form?.city },
      ],
    },
    {
      label: t('sharedDriverLicence'),
      title: t('sharedDriverLicence'),
      description: t('wizardLicenseDescription'),
      icon: <BadgeIcon sx={{ fontSize: 18 }} />,
      component: LicenseStep,
      optional: true,
      getSummary: (data) => [
        { label: t('sharedDocumentCNH'), value: data.licenseNumber },
        { label: t('deviceCategory'), value: data.attributes?.form?.documentCategory },
      ],
    },
    {
      label: t('driverAccountStepCredentials'),
      title: t('driverAccountStepCredentialsTitle'),
      description: t('driverAccountStepCredentialsDesc'),
      icon: <LockIcon sx={{ fontSize: 18 }} />,
      component: AppAccessStep,
      optional: true,
      validate: (data) => {
        if (!data._enableAppAccess) return true;
        const errors = {};
        if (!data.cpf || data.cpf.replace(/[^0-9]/g, '').length !== 11) {
          errors._password = t('driverAccountCpfRequired');
        }
        if (!data._password || data._password.length < 6) {
          errors._password = t('driverAccountPasswordMinLength');
        }
        if (data._password !== data._confirmPassword) {
          errors._confirmPassword = t('driverAccountPasswordMismatch');
        }
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => (data._enableAppAccess
        ? [{ label: t('driverAccountAccess'), value: t('sharedYes') || 'Sim' }]
        : [{ label: t('driverAccountAccess'), value: t('sharedNo') || 'Nao' }]),
    },
    {
      label: t('sharedAttributes'),
      title: t('sharedAttributes'),
      description: t('wizardAttributesDescription') || 'Atributos personalizados do motorista',
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      component: AttributesStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    setSubmitError(null);
    try {
      if (data._enableAppAccess && !driver?.userId) {
        // Create driver + user account via /api/driver/accounts
        const payload = {
          name: data.name,
          cpf: data.cpf,
          phone: data.phone || null,
          password: data._password,
          licenseNumber: data.licenseNumber || null,
          revendaId: data.revendaId || 0,
        };

        const accountResponse = await fetch('/api/driver/accounts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!accountResponse.ok) {
          const errorData = await accountResponse.json().catch(() => ({}));
          throw new Error(errorData.message || t('errorGeneric'));
        }

        const { driver: createdDriver } = await accountResponse.json();

        // Now update the full driver with attributes.form data
        const fullData = {
          ...data,
          id: createdDriver.id,
          userId: createdDriver.userId,
          status: 'active',
        };
        // Remove temporary fields
        delete fullData._password;
        delete fullData._confirmPassword;
        delete fullData._enableAppAccess;

        const updateResponse = await fetch(`/api/drivers/${createdDriver.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fullData),
        });

        if (updateResponse.ok) {
          const finalDriver = await updateResponse.json();
          dispatch(driversActions.refresh([finalDriver]));
          onSave?.(finalDriver);
          // Show QR dialog for new driver with app access
          setSavedDriver(finalDriver);
          setQrDialogOpen(true);
        } else {
          const errorData = await updateResponse.json().catch(() => ({}));
          throw new Error(errorData.message || t('errorGeneric'));
        }
      } else {
        // Standard driver CRUD (no account creation)
        const url = driver?.id ? `/api/drivers/${driver.id}` : '/api/drivers';
        const method = driver?.id ? 'PUT' : 'POST';

        // Clean temporary fields
        const payload = { ...data };
        delete payload._password;
        delete payload._confirmPassword;
        delete payload._enableAppAccess;

        // If password was provided on existing driver with userId, update password
        if (data._enableAppAccess && data._password && driver?.userId) {
          payload.password = data._password;
        }

        const response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const savedDriver = await response.json();
          dispatch(driversActions.refresh([savedDriver]));
          onSave?.(savedDriver);
          navigate('/settings/drivers');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || t('errorGeneric'));
        }
      }
    } catch (error) {
      setSubmitError(error.message);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/drivers');
  };

  const handleQrDialogClose = () => {
    setQrDialogOpen(false);
    setSavedDriver(null);
    navigate('/settings/drivers');
  };

  const initialData = driver
    ? {
      ...driver,
      _enableAppAccess: false,
      _password: '',
      _confirmPassword: '',
    }
    : {
      name: '',
      uniqueId: '',
      cpf: '',
      phone: '',
      licenseNumber: '',
      licenseExpiry: null,
      status: 'active',
      attributes: { form: {} },
      _enableAppAccess: false,
      _password: '',
      _confirmPassword: '',
    };

  return (
    <Box>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}
      <PremiumWizard
        steps={steps}
        initialData={initialData}
        onComplete={handleComplete}
        onCancel={handleCancel}
        storageKey={driver?.id ? null : 'driver-wizard-draft'}
        completeButtonText={driver?.id ? t('sharedSave') : t('sharedAdd')}
        nextButtonText={t('wizardNext')}
        backButtonText={t('sharedBack')}
        cancelButtonText={t('sharedCancel')}
        skipButtonText={t('wizardSkip')}
        showSummary
      />

      {/* QR Code Dialog - shown after creating driver with app access */}
      <DriverQrCodeDialog
        open={qrDialogOpen}
        onClose={handleQrDialogClose}
        driver={savedDriver}
      />
    </Box>
  );
};

export default DriverFormWizard;
